import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PropertyType } from "@/mockData/rooms";
import { roomService } from "@/services/roomService";

interface PropertyTypeFormProps {
  isOpen: boolean;
  onClose: () => void;
  propertyType?: PropertyType | null;
  onSave: (propertyType: PropertyType) => void;
}

const availableIcons = [
  { value: "🏨", label: "Hotel" },
  { value: "🏠", label: "House" },
  { value: "🏛️", label: "Building" },
  { value: "✨", label: "Star" },
  { value: "🏡", label: "Villa" },
  { value: "🏢", label: "Office" },
  { value: "🏖️", label: "Beach" },
  { value: "🏔️", label: "Mountain" },
  { value: "🌊", label: "Water" },
  { value: "🌳", label: "Nature" },
  { value: "🏪", label: "Store" },
  { value: "🏭", label: "Factory" },
];

export function PropertyTypeForm({ isOpen, onClose, propertyType, onSave }: PropertyTypeFormProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name_en: "",
    name_ar: "",
    description_en: "",
    description_ar: "",
    icon: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (propertyType) {
        setFormData({
          name_en: propertyType.name_en,
          name_ar: propertyType.name_ar,
          description_en: propertyType.description_en,
          description_ar: propertyType.description_ar,
          icon: propertyType.icon,
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, propertyType]);

  const resetForm = () => {
    setFormData({
      name_en: "",
      name_ar: "",
      description_en: "",
      description_ar: "",
      icon: "",
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('🔧 PropertyTypeForm.handleSubmit called:', {
        isUpdate: !!propertyType,
        formData,
        propertyTypeId: propertyType?.id
      });
      
      // Call the onSave callback which will handle the API call
      // The parent component (rooms.tsx) handles the actual API calls now
      onSave(formData);
      onClose();
    } catch (error) {
      console.error("Error saving property type:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {propertyType ? t("updatePropertyType") : t("createPropertyType")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Icon Selection */}
          <div className="space-y-2">
            <Label>{t("propertyTypeIcon")}</Label>
            <div className="grid grid-cols-6 gap-2">
              {availableIcons.map((icon) => (
                <button
                  key={icon.value}
                  type="button"
                  className={`w-12 h-12 text-2xl rounded-lg border-2 flex items-center justify-center transition-colors ${
                    formData.icon === icon.value
                      ? 'border-primary bg-primary/10'
                      : 'border-muted hover:border-primary/50'
                  }`}
                  onClick={() => handleInputChange("icon", icon.value)}
                >
                  {icon.value}
                </button>
              ))}
            </div>
            {formData.icon && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl">{formData.icon}</span>
                <span className="text-sm text-muted-foreground">
                  {availableIcons.find(i => i.value === formData.icon)?.label}
                </span>
              </div>
            )}
          </div>

          {/* English Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t("englishInformation")}</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name_en">{t("nameEnglish")}</Label>
                <Input
                  id="name_en"
                  value={formData.name_en}
                  onChange={(e) => handleInputChange("name_en", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description_en">{t("descriptionEnglish")}</Label>
                <Textarea
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => handleInputChange("description_en", e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Arabic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t("arabicInformation")}</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name_ar">{t("nameArabic")}</Label>
                <Input
                  id="name_ar"
                  value={formData.name_ar}
                  onChange={(e) => handleInputChange("name_ar", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description_ar">{t("descriptionArabic")}</Label>
                <Textarea
                  id="description_ar"
                  value={formData.description_ar}
                  onChange={(e) => handleInputChange("description_ar", e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={loading || !formData.icon || !formData.name_en || !formData.name_ar}>
              {loading ? t("loading") : (propertyType ? t("save") : t("createPropertyType"))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
